import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads, adminWrites } from "../api/admin-api.js";
import type { AdminRole, DeliveryDeltaView, ForecastSeries, ProviderAccount, TermPricesView, TopupSettings, VenueForecastView } from "../api/types.js";
import { count, trx } from "../format.js";
import { Blank, Button, ErrorNote, Note, Panel, Pill, ScreenHeader, Spinner, Table, Td, Th, TxId } from "../ui.js";

/**
 * Prepaid credit per provider and the top-up policy (docs/product/mocks/admin-wallets-providers-mock.html).
 * Reads `GET /v1/admin/providers` (viewer); saves through `PATCH /v1/admin/providers/:provider`
 * (editor, audited). A viewer sees the same table read-only.
 */
const SUN = 1_000_000;
/**
 * No business upper bound on a top-up amount (owner 2026-10-23: the 10,000 TRX bound is gone); only what stays exact
 * as a SUN number in the browser.
 */
const MAX_TRX = Math.floor(Number.MAX_SAFE_INTEGER / SUN);

function agoMinutes(iso: string | null): string {
  if (iso === null) return "never read";
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  return `read ${minutes} min ago`;
}

function shortTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")} ${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}

const asTrx = (sun: number | null): string => (sun === null ? "" : String(sun / SUN));

/** "" → undefined (not set); otherwise a TRX amount of 0 or more, or an error. */
function parseTrx(value: string): { sun?: number; error?: string } {
  if (value.trim() === "") return {};
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0 || n > MAX_TRX) return { error: "A TRX amount, 0 or more" };
  return { sun: Math.round(n * SUN) };
}

function health(row: ProviderAccount, defaultLowWaterSun: number | null): { value: string; tone: "good" | "attention" | "bad" | "neutral" } {
  if (row.live === false) return { value: "not live", tone: "neutral" };
  if (!row.enabled) return { value: "off", tone: "neutral" };
  if (!row.health_ok) return { value: `${row.consecutive_failures} failures`, tone: "bad" };
  const low = row.low_water_sun ?? defaultLowWaterSun;
  if (row.balance_sun !== null && low !== null && row.balance_sun < low) {
    return { value: "low", tone: "attention" };
  }
  return { value: "healthy", tone: "good" };
}

/** Why a venue's switch is disabled: it is not in the worker's env list (owner 2026-10-17). */
const NOT_LIVE_TITLE = "Not in the worker's PROVIDER_LIVE_ALLOWLIST: its top-up policy can be set now, the switch works once it is added there";

function ProviderRow({
  row,
  editable,
  defaultLowWaterSun,
  onSaved,
}: {
  row: ProviderAccount;
  editable: boolean;
  defaultLowWaterSun: number | null;
  onSaved: () => void;
}) {
  // A venue outside the worker's list is listed so its policy can be set ahead, its switch disabled and off.
  const live = row.live !== false;
  const [enabled, setEnabled] = useState(live && row.enabled);
  const [lowWater, setLowWater] = useState(asTrx(row.low_water_sun));
  const [cap, setCap] = useState(asTrx(row.daily_topup_cap_sun));
  const [target, setTarget] = useState(asTrx(row.topup_target_sun ?? null));
  const [saving, setSaving] = useState(false);
  const low = parseTrx(lowWater);
  const capParsed = parseTrx(cap);
  const targetParsed = parseTrx(target);
  // The level to refill to cannot sit below the mark that triggers the refill (the API refuses it too).
  const lowSun = low.sun ?? row.low_water_sun;
  const targetError =
    targetParsed.error ??
    (targetParsed.sun !== undefined && lowSun !== null && targetParsed.sun < lowSun ? "At least the low-water mark" : undefined);
  const targetChanged = target !== asTrx(row.topup_target_sun ?? null);
  const dirty =
    (live && enabled !== row.enabled) ||
    lowWater !== asTrx(row.low_water_sun) ||
    cap !== asTrx(row.daily_topup_cap_sun) ||
    targetChanged;
  const invalid = low.error !== undefined || capParsed.error !== undefined || targetError !== undefined;
  const h = health({ ...row, enabled }, defaultLowWaterSun);
  const inputClass = (changed: boolean, bad: boolean) =>
    `num min-h-8 w-[84px] rounded-[12px] border bg-surface-raised px-2.5 text-right text-[12px] text-ink focus:border-accent focus:outline-none ${
      bad ? "border-danger" : changed ? "border-accent/70" : "border-line"
    }`;

  const save = async () => {
    setSaving(true);
    try {
      await adminWrites.updateProviderPolicy(row.provider, {
        ...(live && enabled !== row.enabled ? { enabled } : {}),
        ...(lowWater !== asTrx(row.low_water_sun) && low.sun !== undefined ? { low_water_sun: low.sun } : {}),
        ...(cap !== asTrx(row.daily_topup_cap_sun) && capParsed.sun !== undefined ? { daily_topup_cap_sun: capParsed.sun } : {}),
        // An emptied field clears "top up to" (the target then follows our spend again).
        ...(targetChanged ? { topup_target_sun: targetParsed.sun ?? null } : {}),
      });
      onSaved();
    } catch {
      // toasted by `announced`
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr class={enabled ? "" : "opacity-60"} title={live ? undefined : NOT_LIVE_TITLE}>
      <Td>
        <span class="font-medium">{row.display_name ?? row.provider}</span>
      </Td>
      <Td>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label={`${row.provider} on`}
          disabled={!editable || !live}
          title={live ? undefined : NOT_LIVE_TITLE}
          onClick={() => setEnabled(!enabled)}
          class={`relative h-5 w-[34px] rounded-full border transition-colors ${enabled ? "border-accent bg-accent" : "border-line bg-surface-raised"} disabled:cursor-not-allowed`}
        >
          <span class={`absolute top-0.5 h-3.5 w-3.5 rounded-full transition-all ${enabled ? "left-4 bg-white" : "left-0.5 bg-ink-muted"}`} />
        </button>
      </Td>
      <Td numeric>
        {row.balance_sun === null ? <Blank title="Balance never read" /> : trx(row.balance_sun, 1)}
        <span class="block text-[11px] text-ink-muted">{agoMinutes(row.balance_read_at)}</span>
      </Td>
      <Td numeric>{row.spend_24h_sun === undefined ? <Blank /> : trx(row.spend_24h_sun, 1)}</Td>
      <Td>
        <input
          class={inputClass(lowWater !== asTrx(row.low_water_sun), low.error !== undefined)}
          inputMode="decimal"
          value={lowWater}
          placeholder={defaultLowWaterSun === null ? undefined : asTrx(defaultLowWaterSun)}
          disabled={!editable}
          title={low.error}
          aria-label={`${row.provider} low-water, TRX`}
          onInput={(e) => setLowWater((e.target as HTMLInputElement).value)}
        />
      </Td>
      <Td>
        <input
          class={inputClass(targetChanged, targetError !== undefined)}
          inputMode="decimal"
          value={target}
          disabled={!editable}
          title={targetError}
          aria-label={`${row.provider} top up to, TRX`}
          onInput={(e) => setTarget((e.target as HTMLInputElement).value)}
        />
      </Td>
      <Td>
        <input
          class={inputClass(cap !== asTrx(row.daily_topup_cap_sun), capParsed.error !== undefined)}
          inputMode="decimal"
          value={cap}
          disabled={!editable}
          title={capParsed.error}
          aria-label={`${row.provider} daily cap, TRX`}
          onInput={(e) => setCap((e.target as HTMLInputElement).value)}
        />
      </Td>
      <Td mono>
        {row.last_topup == null ? (
          <Blank title="No top-up recorded" />
        ) : (
          <>
            <span class="block">
              {row.last_topup.amount_sun === null ? "—" : trx(row.last_topup.amount_sun, 1)} ·{" "}
              {shortTime(row.last_topup.at)}
            </span>
            <TxId value={row.last_topup.txid} />
          </>
        )}
      </Td>
      <Td>{row.last_decision == null ? <Blank title="The worker stores no decision yet" /> : row.last_decision}</Td>
      <Td>
        <Pill value={h.value} tone={h.tone} />
      </Td>
      <Td>
        {editable ? (
          <Button disabled={!dirty || invalid || saving} onClick={() => void save()}>
            {saving ? "Saving…" : "Save"}
          </Button>
        ) : null}
      </Td>
    </tr>
  );
}

/**
 * The global "Minimum top-up" (owner 2026-10-15): no top-up is ever smaller. Reads `GET /v1/admin/topup-settings`,
 * saves through `PATCH` (editor, audited); an emptied field returns to the worker default.
 */
function DefaultLowWater({ settings, editable, onSaved }: { settings: TopupSettings; editable: boolean; onSaved: () => void }) {
  const stored = asTrx(settings.default_low_water_sun ?? null);
  const [value, setValue] = useState(stored);
  const [saving, setSaving] = useState(false);
  const parsed = parseTrx(value);
  const error = parsed.error ?? (parsed.sun !== undefined && parsed.sun < 1 ? "More than 0 TRX" : undefined);
  const dirty = value !== stored;
  const save = async () => {
    setSaving(true);
    try {
      await adminWrites.updateTopupSettings({ default_low_water_sun: parsed.sun ?? null });
      onSaved();
    } catch {
      // toasted by `announced`
    } finally {
      setSaving(false);
    }
  };
  return (
    <div class="mb-3 flex flex-wrap items-center gap-2 text-[12px]">
      <label for="default-low-water" class="text-ink">Low-water by default, TRX</label>
      <input
        id="default-low-water"
        class={`num min-h-8 w-[84px] rounded-[12px] border bg-surface-raised px-2.5 text-right text-[12px] text-ink focus:border-accent focus:outline-none ${
          error !== undefined ? "border-danger" : dirty ? "border-accent/70" : "border-line"
        }`}
        inputMode="decimal"
        value={value}
        disabled={!editable}
        title={error}
        onInput={(e) => setValue((e.target as HTMLInputElement).value)}
      />
      {editable ? (
        <Button disabled={!dirty || error !== undefined || saving} onClick={() => void save()}>
          {saving ? "Saving…" : "Save"}
        </Button>
      ) : null}
      <span class="text-ink-muted">For a provider whose own Low-water is empty.</span>
    </div>
  );
}

function MinimumTopup({ settings, editable, onSaved }: { settings: TopupSettings; editable: boolean; onSaved: () => void }) {
  const stored = asTrx(settings.min_transfer_sun);
  const [value, setValue] = useState(stored);
  const [saving, setSaving] = useState(false);
  const parsed = parseTrx(value);
  const error = parsed.error ?? (parsed.sun !== undefined && parsed.sun < 1 ? "More than 0 TRX" : undefined);
  const dirty = value !== stored;
  const save = async () => {
    setSaving(true);
    try {
      await adminWrites.updateTopupSettings({ min_transfer_sun: parsed.sun ?? null });
      onSaved();
    } catch {
      // toasted by `announced`
    } finally {
      setSaving(false);
    }
  };
  return (
    <div class="mb-3 flex flex-wrap items-center gap-2 text-[12px]">
      <label for="min-topup" class="text-ink">Minimum top-up, TRX</label>
      <input
        id="min-topup"
        class={`num min-h-8 w-[84px] rounded-[12px] border bg-surface-raised px-2.5 text-right text-[12px] text-ink focus:border-accent focus:outline-none ${
          error !== undefined ? "border-danger" : dirty ? "border-accent/70" : "border-line"
        }`}
        inputMode="decimal"
        value={value}
        placeholder={asTrx(settings.default_min_transfer_sun)}
        disabled={!editable}
        title={error}
        onInput={(e) => setValue((e.target as HTMLInputElement).value)}
      />
      {editable ? (
        <Button disabled={!dirty || error !== undefined || saving} onClick={() => void save()}>
          {saving ? "Saving…" : "Save"}
        </Button>
      ) : null}
      <span class="text-ink-muted">
        {settings.min_transfer_sun === null ? `Default: ${asTrx(settings.default_min_transfer_sun)} TRX. ` : ""}
        No top-up is ever smaller.
      </span>
    </div>
  );
}

const signed = (n: number) => (n > 0 ? `+${count(n)}` : n < 0 ? `−${count(-n)}` : "0");

/** Delivered vs ordered per venue: TRX turns into energy at a ratio that moves every block (owner 2026-10-21). */
function DeliveryDelta() {
  const [days, setDays] = useState<7 | 30>(30);
  const data = useResource((signal) => {
    void signal;
    return adminReads.deliveryDelta(days);
  }, [days]);
  const view: DeliveryDeltaView | null = data.data ?? null;
  return (
    <Panel
      title="Delivered vs ordered"
      subtitle="Units the chain shows delegated minus units we ordered, per provider delivery. Venues delegate TRX, and the energy it buys moves a little every block."
    >
      <div class="mb-3 flex gap-1">
        {([7, 30] as const).map((d) => (
          <Button key={d} tone={days === d ? "primary" : "ghost"} onClick={() => setDays(d)}>
            {d} days
          </Button>
        ))}
      </div>
      {data.loading && view === null ? (
        <Spinner />
      ) : data.error !== null && view === null ? (
        <ErrorNote error={data.error} onRetry={() => data.reload()} />
      ) : (
        <Table
          rows={view?.data.length ?? 0}
          empty="No provider delivery proven on chain in this period."
          head={
            <tr>
              <Th>Provider</Th>
              <Th>Resource</Th>
              <Th numeric>Deliveries</Th>
              <Th numeric>Exact</Th>
              <Th numeric>Short</Th>
              <Th numeric>Over</Th>
              <Th numeric>Typical</Th>
              <Th numeric>Worst short</Th>
              <Th numeric>Worst short, ppm</Th>
              <Th numeric>Most over</Th>
            </tr>
          }
        >
          {(view?.data ?? []).map((row) => (
            <tr key={`${row.provider}:${row.resource}`}>
              <Td>{row.provider}</Td>
              <Td>{row.resource}</Td>
              <Td numeric>{count(row.deliveries)}</Td>
              <Td numeric>{count(row.exact)}</Td>
              <Td numeric>{count(row.short)}</Td>
              <Td numeric>{count(row.over)}</Td>
              <Td numeric>{signed(row.median_delta)}</Td>
              <Td numeric>{row.min_delta < 0 ? signed(row.min_delta) : "—"}</Td>
              <Td numeric>{row.min_delta < 0 ? row.min_delta_ppm.toLocaleString("en-US") : "—"}</Td>
              <Td numeric>{row.max_delta > 0 ? signed(row.max_delta) : "—"}</Td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}

const PARAMS = ["price", "min", "max", "available", "fee", "utilization", "capacity", "config"] as const;

function forecastValue(series: ForecastSeries, v: number | null): string {
  if (v === null) return "—";
  if (series.unit === "percent") return `${v.toFixed(0)} %`;
  if (series.unit === "value") return v.toLocaleString("en-US");
  return series.unit === "sun_per_unit" ? v.toFixed(2) : count(Math.round(v));
}

/** One row per series: this hour, and the lowest and highest hour of the next 24 (owner 2026-10-21). */
function VenueForecasts() {
  const [param, setParam] = useState<(typeof PARAMS)[number]>("price");
  const data = useResource((signal) => {
    void signal;
    return adminReads.venueForecasts();
  }, []);
  const view: VenueForecastView | null = data.data ?? null;
  // «config»: every number of a venue's public settings, one row each.
  const rows = (view?.series ?? []).filter((s) => (param === "config" ? s.param.startsWith("config:") : s.param === param));
  const extreme = (s: ForecastSeries, pick: "min" | "max") =>
    s.hours.reduce<{ hour: number; value: number } | null>((best, h) => {
      if (h.value === null) return best;
      if (best === null || (pick === "min" ? h.value < best.value : h.value > best.value)) return { hour: h.hour, value: h.value };
      return best;
    }, null);
  return (
    <Panel
      title="Forecast, next 24 h"
      subtitle={`The price map's model on every series we keep a minute history of: same day type, the last ${view?.lookback_days ?? 21} days, the newest weighing most. Hours are UTC.`}
    >
      <div class="mb-3 flex flex-wrap gap-1">
        {PARAMS.map((p) => (
          <Button key={p} tone={param === p ? "primary" : "ghost"} onClick={() => setParam(p)}>
            {p}
          </Button>
        ))}
      </div>
      {data.loading && view === null ? (
        <Spinner />
      ) : data.error !== null && view === null ? (
        <ErrorNote error={data.error} onRetry={() => data.reload()} />
      ) : (
        <Table
          rows={rows.length}
          empty="No history for this parameter yet."
          head={
            <tr>
              <Th>Source</Th>
              <Th>Mode</Th>
              <Th>Resource</Th>
              <Th>Window</Th>
              <Th>From</Th>
              <Th numeric>This hour</Th>
              <Th numeric>Lowest (hour)</Th>
              <Th numeric>Highest (hour)</Th>
            </tr>
          }
        >
          {rows.map((s) => {
            const low = extreme(s, "min");
            const high = extreme(s, "max");
            return (
              <tr key={`${s.origin}:${s.source}:${s.resource}:${s.tier ?? ""}:${s.param}`}>
                <Td>{s.source}</Td>
                <Td>{s.mode}</Td>
                <Td>{s.resource}</Td>
                <Td>{s.param.startsWith("config:") ? s.param.slice("config:".length) : (s.tier ?? "—")}</Td>
                <Td>{s.origin === "market_prices" ? "board" : s.origin === "venue_quote_history" ? "router" : s.origin === "refill_pool_history" ? "pool" : "config"}</Td>
                <Td numeric>{forecastValue(s, s.hours[0]?.value ?? null)}</Td>
                <Td numeric>{low === null ? "—" : `${forecastValue(s, low.value)} (${String(low.hour).padStart(2, "0")}:00)`}</Td>
                <Td numeric>{high === null ? "—" : `${forecastValue(s, high.value)} (${String(high.hour).padStart(2, "0")}:00)`}</Td>
              </tr>
            );
          })}
        </Table>
      )}
    </Panel>
  );
}

/**
 * Every venue's price per rental term (owner 2026-11-01: «мониторинг … других периодов»): one row per venue, one column
 * per term it answered for in the last 7 days, the cheapest of each column marked. The price is SUN per unit for the
 * whole term; a fixed fee per order is shown beside it. A cell's title says when the answer last changed.
 */
function TermMatrix() {
  const [resource, setResource] = useState<"energy" | "bandwidth">("energy");
  const data = useResource((signal) => {
    void signal;
    return adminReads.termPrices();
  }, []);
  const view: TermPricesView | null = data.data ?? null;
  const prices = (view?.prices ?? []).filter((p) => p.resource === resource && p.unit_sun !== null);
  const tiers = (view?.tiers ?? []).filter((t) => prices.some((p) => p.tier === t));
  const venues = [...new Set(prices.map((p) => p.source))].sort();
  const cell = new Map(prices.map((p) => [`${p.source}|${p.tier}`, p]));
  const cheapest = new Map(tiers.map((t) => [t, Math.min(...prices.filter((p) => p.tier === t).map((p) => p.unit_sun!))]));
  return (
    <Panel
      title="Prices by term"
      subtitle="Each venue's newest answer per rental term over the last 7 days, SUN per unit for the whole term (+ its fee per order). Terms we do not sell are read every 10 minutes for this; the cheapest of each term is marked."
    >
      <div class="mb-3 flex flex-wrap gap-1">
        {(["energy", "bandwidth"] as const).map((r) => (
          <Button key={r} tone={resource === r ? "primary" : "ghost"} onClick={() => setResource(r)}>
            {r}
          </Button>
        ))}
      </div>
      {data.loading && view === null ? (
        <Spinner />
      ) : data.error !== null && view === null ? (
        <ErrorNote error={data.error} onRetry={() => data.reload()} />
      ) : (
        <Table
          rows={venues.length}
          empty="No venue answered for this resource in the last 7 days."
          head={
            <tr>
              <Th>Venue</Th>
              {tiers.map((t) => (
                <Th key={t} numeric>
                  {t}
                </Th>
              ))}
            </tr>
          }
        >
          {venues.map((venue) => (
            <tr key={venue}>
              <Td>{venue}</Td>
              {tiers.map((t) => {
                const p = cell.get(`${venue}|${t}`);
                if (p === undefined) return <Td key={t} numeric>—</Td>;
                const best = p.unit_sun === cheapest.get(t);
                return (
                  <Td key={t} numeric>
                    <span
                      class={best ? "font-semibold text-accent-text" : undefined}
                      title={`changed ${shortTime(p.changed_at)} UTC · min ${count(p.min_amount ?? 0)}`}
                    >
                      {p.unit_sun!.toFixed(2)}
                      {p.fixed_sun > 0 ? ` +${trx(p.fixed_sun)}` : ""}
                    </span>
                  </Td>
                );
              })}
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}

export function ProvidersScreen({ role }: { role: AdminRole }) {
  const providers = useResource((signal) => {
    void signal;
    return adminReads.providers();
  }, []);
  const settings = useResource((signal) => {
    void signal;
    return adminReads.topupSettings();
  }, []);
  const rows = providers.data?.data ?? [];
  const editable = role === "editor";
  return (
    <>
      <ScreenHeader
        title="Providers"
        lead="Prepaid credit at each provider. When its credit falls below the low-water mark, the collector tops it up to the Top up to level, never more than the daily cap and never less than the minimum top-up."
      />
      <Panel title="Prepaid credit" subtitle="Credit as each provider reports it. Thresholds are in TRX.">
        {settings.data != null ? (
          <>
            <MinimumTopup
              key={String(settings.data.min_transfer_sun)}
              settings={settings.data}
              editable={editable}
              onSaved={() => settings.reload()}
            />
            <DefaultLowWater
              key={`low:${String(settings.data.default_low_water_sun ?? null)}`}
              settings={settings.data}
              editable={editable}
              onSaved={() => settings.reload()}
            />
          </>
        ) : settings.error !== null ? (
          <ErrorNote error={settings.error} onRetry={() => settings.reload()} />
        ) : null}
        {providers.loading && providers.data == null ? (
          <Spinner />
        ) : providers.error !== null ? (
          <ErrorNote error={providers.error} onRetry={() => providers.reload()} />
        ) : (
          <Table
            rows={rows.length}
            empty="No provider account exists yet. Providers are onboarded per docs/ops/providers-onboarding.md."
            head={
              <tr>
                <Th>Provider</Th>
                <Th>On</Th>
                <Th numeric>Credit</Th>
                <Th numeric>Our spend 24 h</Th>
                <Th>Low-water</Th>
                <Th>Top up to</Th>
                <Th>Daily cap</Th>
                <Th>Last top-up</Th>
                <Th>Last decision</Th>
                <Th>Health</Th>
                <Th>{""}</Th>
              </tr>
            }
          >
            {rows.map((row) => (
              <ProviderRow
                key={`${row.provider}:${row.updated_at ?? ""}`}
                row={row}
                editable={editable}
                defaultLowWaterSun={settings.data?.default_low_water_sun ?? null}
                onSaved={() => providers.reload()}
              />
            ))}
          </Table>
        )}
        <div class="mt-2">
          <Note>
            A provider is topped up when its credit falls below Low-water, back to Top up to, or to what our orders
            spend there in a day if that is more. A top-up is at most twice what our orders spent at the provider since
            its last confirmed top-up (the last 24 hours if it was never topped up), but never below the minimum
            top-up: a smaller need, or a smaller allowance, is sent as the minimum. A provider our orders spent nothing
            at since its last top-up gets nothing. Every top-up stays within the daily cap (a cap of 0 stops top-ups)
            and the top-up wallet; a wallet holding less than the minimum skips it, and Last decision says why. Our
            spend 24 h is what our orders spent there in the last 24 hours.
          </Note>
        </div>
      </Panel>
      <DeliveryDelta />
      <TermMatrix />
      <VenueForecasts />
    </>
  );
}
